import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Sparkle } from "@phosphor-icons/react";

export default function PastDue() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [notice, setNotice] = useState({ subject: "", body: "" });
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch { toast.error("Failed to load tenants"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNotice = (t) => {
    setActive(t);
    setNotice({ subject: "", body: "" });
  };

  const draftAndSend = async () => {
    if (!active) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/tenants/${active.id}/notify`, { channel: "email" });
      setNotice({ subject: data.subject, body: data.body });
      toast.success(`Notice sent to ${active.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Notify failed"); }
    setWorking(false);
  };

  const totalDue = tenants.reduce((acc, t) => acc + (t.balance_due || 0), 0);
  const overdueCount = tenants.filter(t => t.balance_due > 0).length;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="past-due-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Rent Collection</div>
          <h1 className="font-heading text-4xl font-semibold text-stone-900 mt-1">Past-Due Balances</h1>
          <p className="text-stone-500 mt-1">Send AI-drafted collection notices with the right tone.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Total outstanding</div>
          <div className="font-heading text-3xl font-semibold text-terra-500">${totalDue.toLocaleString()}</div>
          <div className="text-sm text-stone-500">{overdueCount} tenant{overdueCount !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {loading && <div className="text-stone-400">Loading…</div>}

      <Card className="border-stone-200 rounded-xl shadow-none bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 bg-sand-50 border-b border-stone-200 text-xs uppercase tracking-[0.18em] text-stone-400">
          <div className="col-span-4">Tenant</div>
          <div className="col-span-4">Property</div>
          <div className="col-span-1 text-right">Days</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-1"></div>
        </div>
        <div className="divide-y divide-stone-100">
          {tenants.map((t) => (
            <div
              key={t.id}
              className="grid grid-cols-12 px-6 py-4 items-center hover:bg-sand-50 transition-colors"
              data-testid={`tenant-row-${t.id}`}
            >
              <div className="col-span-4">
                <div className="font-medium text-stone-900">{t.name}</div>
                <div className="text-xs text-stone-500">{t.email}</div>
              </div>
              <div className="col-span-4 text-sm text-stone-600">{t.property_address}</div>
              <div className="col-span-1 text-right text-sm text-stone-600">
                {t.days_overdue > 0 ? `${t.days_overdue}d` : "—"}
              </div>
              <div className="col-span-2 text-right">
                <span className={`font-heading text-lg ${t.balance_due > 0 ? "text-terra-500" : "text-stone-500"}`}>
                  ${t.balance_due.toLocaleString()}
                </span>
              </div>
              <div className="col-span-1 flex justify-end">
                {t.balance_due > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openNotice(t)}
                    className="border-stone-300"
                    data-testid={`notify-btn-${t.id}`}
                  >
                    <Bell size={14} className="mr-1" /> Notify
                  </Button>
                ) : (
                  <span className="text-xs text-stone-400">Current</span>
                )}
              </div>
            </div>
          ))}
          {tenants.length === 0 && (
            <div className="p-10 text-center text-stone-500">No tenants yet.</div>
          )}
        </div>
      </Card>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="notify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Past-Due Notice — {active?.name}</DialogTitle>
            <div className="text-sm text-stone-500">
              Balance ${active?.balance_due?.toLocaleString()} · {active?.days_overdue} days overdue
            </div>
          </DialogHeader>
          {notice.body ? (
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-1">Subject</div>
                <div className="border border-stone-200 rounded-md px-3 py-2 bg-sand-50 text-sm">{notice.subject}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-1">Body</div>
                <Textarea readOnly value={notice.body} rows={10} data-testid="notify-body-preview" />
              </div>
            </div>
          ) : (
            <p className="text-sm text-stone-600">
              Click below to have the AI draft a firm, professional notice and send it to {active?.email}.
            </p>
          )}
          <DialogFooter>
            {!notice.body ? (
              <Button onClick={draftAndSend} disabled={working} className="bg-forest-600 hover:bg-forest-500 text-white" data-testid="notify-draft-send-btn">
                <Sparkle size={16} className="mr-2" /> {working ? "Sending…" : "Draft & Send"}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setActive(null)} data-testid="notify-close-btn">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
